import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
} from '@mui/material';
import moment from 'moment';
import log from 'loglevel';
import { DATE_FORMAT } from '../../../../consts';
import { MatchFlowStatusEnum, PROGRESS_STATUS } from '../../../../consts/enums';
import { useGetLastMatchFlow } from '../../../../hooks/query/useMatchFlows';
import { useEngineRunCancelMatchFlow, useEngineRunMatchFlow } from '../../../../hooks/usePositions';
import { RunStatus } from './RunStatus';
import { RunStatusInfo } from './RunStatusInfo';
import { style } from './style';
import { RunActions } from './RunActions';

const TAG = '[ENGINE_RUN_STATUS]';

type EngineRunStatusProps = {
  accountId: string,
  positionId: string,
}
export const EngineRunStatus = ({
  accountId,
  positionId,
}:EngineRunStatusProps):JSX.Element => {
  const lastRunData = useGetLastMatchFlow(accountId, positionId);

  const {
    mutate: runNewMatchFlow,
    isError: isRunNewMatchFlowError,
    error: runNewMatchFlowError,
    isLoading: isRunNewMatchFlowLoading,
  } = useEngineRunMatchFlow(lastRunData.refetch);

  const {
    mutate: cancelMatchFlow,
    isLoading: isCancelMatchFlowLoading,
  } = useEngineRunCancelMatchFlow(lastRunData.refetch);

  const hasLastRun = !!lastRunData.data?._id;
  const updatedAt = hasLastRun ?
    moment(lastRunData.data.updatedAt).format(DATE_FORMAT.TABLE_LIST) :
    '';

  const handleStartRun = () => {
    runNewMatchFlow({ accountId, positionId });
  };

  const getStatus = ():string | null => {
    if (lastRunData.isError || lastRunData.data?.status === MatchFlowStatusEnum.Failed || isRunNewMatchFlowError) {
      return PROGRESS_STATUS.ERROR;
    }
    if (hasLastRun && lastRunData.data) {
      return lastRunData.data.status;
    }

    return null;
  };
  const statusResult = getStatus();

  const getErrorMessage = ():string => {
    // error from last run API call
    if (lastRunData.isError || lastRunData.data?.status === MatchFlowStatusEnum.Failed) {
      log.debug(TAG, 'lastRunData has error', lastRunData);

      return (
        lastRunData.data?.errorMessage ||
        lastRunData.data?.matchEngine?.errorPayload?.err ||
        lastRunData.error?.response?.data.message
      );
    }

    // error from running new match flow API call
    if (isRunNewMatchFlowError) {
      log.debug(TAG, 'runNewMatchFlow has error', runNewMatchFlow);

      return runNewMatchFlowError?.response?.data.message;
    }

    // error from last run status
    if (lastRunData?.data?.errorMessage) {
      log.debug(TAG, 'found error message in run status');

      return lastRunData?.data?.errorMessage;
    }

    // no errors that we know about
    return '';
  };

  const handleAbortRunSearch = () => {
    if (!lastRunData.data.matchEngine?.runId) {
      return;
    }
    const matchFLowRunId = lastRunData.data._id;

    cancelMatchFlow({ accountId, positionId, matchFLowRunId });
  };

  return (
    <Card sx={ style.Card.sx }>
      <CardHeader
        title={ (
          <Box sx={ style.row.sx }>
            <RunStatusInfo
              id={ lastRunData.data?.matchEngine?.runId || '' }
              hasRun={ hasLastRun }
              date={ updatedAt }
            />
            <RunActions
              onRunClick={ handleStartRun }
              isMatchFlowRunLoading={ isRunNewMatchFlowLoading }
              isMatchFlowCancelLoading={ isCancelMatchFlowLoading }
              isMatchFlowRunning={
                statusResult ===  PROGRESS_STATUS.IN_PROGRESS
              }
              onAbortRunSearchClick={ handleAbortRunSearch }
            />
          </Box>
        ) }
      />
      {statusResult && (
        <>
          <Divider />
          <CardContent
            sx={ style.CardContent }
          >
            <RunStatus
              status={ statusResult }
              progress={ lastRunData.data?.matchEngine?.progress || 0 }
              errorMessage={ getErrorMessage() }
              matchEngineStatus={ lastRunData?.data?.matchEngine?.status }
              numberOfCreatedCandidates={ lastRunData.data?.numberOfCreatedCandidates }
              duplicateCandidatesSkipped={ lastRunData.data?.duplicateCandidatesSkipped }
            />

          </CardContent>
          <Divider sx={ style.Divider.sx } />
        </>
      )}
    </Card>
  );
};
